import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  markCaptionsSessionStarted,
  unmarkCaptionsSessionStarted,
} from './callCaptionsSlice';
import { selectCaptionsEnabled } from './selectors';

import type { RootState } from 'features/app/store/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { selectIsConnected } from 'features/call/call-ui-state/store';
import { VoiceSessionDaoService } from 'features/voice-session/services';
import { voiceSessionIdSelector } from 'features/voice-session/store';

interface CaptionsSessionParams {
  source: string;
}

export const startCaptionsSessionThunk = createAsyncThunk(
  'callCaptions/startSession',
  async ({ source }: CaptionsSessionParams, { dispatch, getState }) => {
    const state = getState() as RootState;
    const voiceSessionId = voiceSessionIdSelector(state);
    const isCallConnected = selectIsConnected(state);
    const captionsEnabled = selectCaptionsEnabled(state);
    const alreadyStarted =
      state.callCaptions.startedSessionIds.includes(voiceSessionId);

    if (
      !captionsEnabled ||
      !isCallConnected ||
      !voiceSessionId ||
      alreadyStarted
    ) {
      return;
    }

    // Mark before the API so concurrent callers dedupe; unmark if start fails.
    dispatch(markCaptionsSessionStarted(voiceSessionId));

    dispatch(
      sendAnalyticsInfo({
        Method: 'startCaptionsSessionThunk',
        Message: 'Calling start-captions API',
        properties: { voiceSessionId, source },
      })
    );

    try {
      await VoiceSessionDaoService.startCaptions(voiceSessionId);
    } catch (error) {
      dispatch(unmarkCaptionsSessionStarted(voiceSessionId));
      console.warn('Voice captions failed to start', {
        error,
        voiceSessionId,
      });
      throw error;
    }
  }
);

export const stopCaptionsSessionThunk = createAsyncThunk(
  'callCaptions/stopSession',
  async ({ source }: CaptionsSessionParams, { dispatch, getState }) => {
    const state = getState() as RootState;
    const voiceSessionId = voiceSessionIdSelector(state);

    if (!voiceSessionId) {
      return;
    }

    const wasStarted =
      state.callCaptions.startedSessionIds.includes(voiceSessionId);

    if (!wasStarted) {
      return;
    }

    dispatch(unmarkCaptionsSessionStarted(voiceSessionId));

    dispatch(
      sendAnalyticsInfo({
        Method: 'stopCaptionsSessionThunk',
        Message: 'Calling stop-captions API',
        properties: { voiceSessionId, source },
      })
    );

    try {
      await VoiceSessionDaoService.stopCaptions(voiceSessionId);
    } catch (error) {
      console.warn('Voice captions failed to stop', {
        error,
        voiceSessionId,
      });
      throw error;
    }
  }
);
